import { useCallback, useMemo, useState } from 'react';
import { Tabs, EmptyState } from '@carboledger/ui';
import { SummaryTiles } from '@/components/common';
import { useTranslations } from '@/hooks';
import { FACILITY_TYPES } from '@/lib/mappers';
import { EMPTY_LIST } from '@/lib/utils';
import { formatDateInput } from '@/lib/format/date';
import { useGetSiteMbPeriodOptionsQuery } from '@/lib/api/lookupsApi';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import { INVENTORY_SUMMARY_TABS } from '../constants/inventory.constants';
import { MassBalanceSummaryView } from './MassBalanceSummaryView';
import { ReconciliationSummaryView } from './ReconciliationSummaryView';
import { MovementSummaryView } from './MovementSummaryView';
import { SummaryDateRange } from './SummaryDateRange';
import {
  currentPeriodRange,
  isRangeInvalid as isInvalidRange,
  buildSummaryReportArgs,
  buildReconciliationArgs,
} from '../constants/summaryRange';

/**
 * Props:
 *   item             the inventory row's facility, material and terminal
 *   units            { declaration, working } unit symbols of this material
 *   module           the route's module segment, for the physical balance save
 *   certificateType  the active module's certificate type
 */
export const InventorySummaryTabView = ({ item, units, module, certificateType }) => {
  const t = useTranslations();
  const [activeSubTab, setActiveSubTab] = useState(INVENTORY_SUMMARY_TABS.movement);
  // Null until the user edits it, so the default follows the period list once it loads.
  const [range, setRange] = useState(null);

  const subTabs = useMemo(
    () => [
      { value: INVENTORY_SUMMARY_TABS.movement, label: t('INVENTORY_SUMMARY_TAB_MOVEMENT') },
      {
        value: INVENTORY_SUMMARY_TABS.reconciliation,
        label: t('INVENTORY_SUMMARY_TAB_RECONCILIATION'),
      },
      {
        value: INVENTORY_SUMMARY_TABS.massBalance,
        label: t('INVENTORY_SUMMARY_TAB_MASS_BALANCE'),
      },
    ],
    [t],
  );

  // All three reports are keyed on a site; a storage or B2B row has no mass balance of its own.
  const isSite = item?.facilityType === FACILITY_TYPES.site;

  const { data: periodOptions = EMPTY_LIST, isLoading: isPeriodsLoading } =
    useGetSiteMbPeriodOptionsQuery(
      { siteId: item?.facilityId, terminalId: item?.terminalId },
      { skip: !isSite },
    );

  const defaultRange = useMemo(
    () => currentPeriodRange(periodOptions, formatDateInput(new Date())),
    [periodOptions],
  );
  const activeRange = range ?? defaultRange;
  // Only a reversed pair is the user's error; an empty one is just still loading.
  const isRangeReversed = Boolean(
    activeRange.from && activeRange.to && isInvalidRange(activeRange),
  );
  const isRangeChanged =
    activeRange.from !== defaultRange.from || activeRange.to !== defaultRange.to;

  const reportArgs = useMemo(
    () => buildSummaryReportArgs({ item, periods: periodOptions, range: activeRange }),
    [item, periodOptions, activeRange],
  );
  const reconciliationArgs = useMemo(
    () => buildReconciliationArgs({ item, periods: periodOptions, range: activeRange }),
    [item, periodOptions, activeRange],
  );
  // Without dates the rows are saved sub-periods; with them they are month buckets with no id.
  const canSaveBalance = Boolean(reconciliationArgs) && !reconciliationArgs.fromDate;

  const header = useMemo(
    () => (
      <Tabs
        tabs={subTabs}
        value={activeSubTab}
        onChange={setActiveSubTab}
        aria-label={t('SECTION_SUMMARY')}
      />
    ),
    [subTabs, activeSubTab, t],
  );

  const handleRangeChange = useCallback((next) => setRange(next), []);

  const dateRange = useMemo(
    () => (
      <SummaryDateRange
        from={activeRange.from}
        to={activeRange.to}
        onChange={handleRangeChange}
        invalid={isRangeReversed}
      />
    ),
    [activeRange, handleRangeChange, isRangeReversed],
  );

  if (!isSite) {
    return (
      <SummaryTiles header={header}>
        <EmptyState
          image={<img src={emptyBoxIllustration} alt="" width={200} height={200} />}
          title={t('INVENTORY_UNAVAILABLE_TITLE')}
          description={t('INVENTORY_UNAVAILABLE_DESCRIPTION')}
        />
      </SummaryTiles>
    );
  }

  // Every report needs a period on the site, whatever range is picked.
  if (!isPeriodsLoading && periodOptions.length === 0) {
    return (
      <SummaryTiles header={header}>
        <EmptyState
          image={<img src={emptyBoxIllustration} alt="" width={200} height={200} />}
          title={t('INVENTORY_PERIOD_REQUIRED_TITLE')}
          description={t('INVENTORY_PERIOD_REQUIRED_DESCRIPTION')}
        />
      </SummaryTiles>
    );
  }

  if (activeSubTab === INVENTORY_SUMMARY_TABS.massBalance) {
    return (
      <MassBalanceSummaryView
        header={header}
        dateRange={dateRange}
        filtered={isRangeChanged}
        args={reportArgs}
        unit={units?.declaration ?? ''}
      />
    );
  }

  if (activeSubTab === INVENTORY_SUMMARY_TABS.reconciliation) {
    return (
      <ReconciliationSummaryView
        header={header}
        dateRange={dateRange}
        filtered={isRangeChanged}
        args={reconciliationArgs}
        module={module}
        certificateType={certificateType}
        materialId={item?.materialId}
        units={units}
        canSave={canSaveBalance}
      />
    );
  }

  return (
    <MovementSummaryView
      header={header}
      dateRange={dateRange}
      filtered={isRangeChanged}
      args={reportArgs}
      units={units}
    />
  );
};
